import {
  Swiper,
  SwiperSlide,
  SwiperOptions,
  Navigation,
  Thumbs,
} from '@components/ui/carousel/slider'
import Image from '@components/ui/image'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { productGalleryPlaceholder } from '@assets/placeholders'
import { getDirection } from '@utils/get-direction'
import { useRouter } from 'next/router'
import CarouselActions from './carousel-actions'
import Share from '@components/product/product-share'
import { useModalAction } from '@components/common/modal/modal.context'
import cartServices from '@services/cartservices'
import { useMutation } from 'react-query'
import ThumbnailZoom from './thumbnail-zoom'
import { toast } from 'react-toastify'
import { IUser } from '@contexts/user/user.utils'
import { UserContext } from '@contexts/user/user.context'

interface Props {
  gallery: any[]
  thumbnailClassName?: string
  galleryClassName?: string
  is_in_stock: boolean
  product: any
  aspectRatio: string
  isPdp?: boolean
  isPlaceholder: boolean
}

// product gallery breakpoints
const galleryCarouselBreakpoints = {
  '0': {
    slidesPerView: 4,
  },
}

const swiperParams: SwiperOptions = {
  slidesPerView: 1,
  spaceBetween: 0,
}

const ThumbnailCarousel: FC<Props> = ({
  gallery = [],
  thumbnailClassName,
  // thumbnailClassName = 'xl:w-[480px] 2xl:w-[200px]',
  galleryClassName = 'xl:w-28 2xl:w-[130px]',
  is_in_stock,
  product,
  aspectRatio,
  isPdp,
  isPlaceholder,
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<any>(null)
  const prevRef = useRef<HTMLDivElement>(null)
  const nextRef = useRef<HTMLDivElement>(null)
  const { locale } = useRouter()
  const dir = getDirection(locale)
  const [selectedImg, setSelectedImg] = useState('0')
  const [showShareIcons, setShowShareIcons] = useState(false)
  const [wishListAdded, setWishListAdded] = useState(false)
  const { openModal } = useModalAction()

  const galleryWithFallback =
    gallery.length > 0
      ? gallery
      : [
          {
            id: 'placeholder',
            original: productGalleryPlaceholder,
            thumbnail: productGalleryPlaceholder,
          },
        ]

  const handleZoomClick = () => {
    openModal('THUMBNAIL_POPUP', { product, gallery })
  }
  const { user } = useContext(UserContext) as IUser

  const { mutate: addWishlistItem, isLoading: whishListLoading } = useMutation(
    async (payload: any) => {
      const response = await cartServices.addItemInWishlist(payload)
      return response
    },
    {
      onSuccess: (data) => {
        setWishListAdded(true)
        toast.success('Product added save for later successfully', {
          position: toast.POSITION.TOP_RIGHT,
          className: 'toast-success',
        })
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message, {
          position: toast.POSITION.TOP_RIGHT,
          className: 'toast-error',
        })
      },
    },
  )

  useEffect(() => {
    if (wishListAdded) {
      const timer = setTimeout(() => {
        setWishListAdded(false)
      }, 5000)

      return () => clearTimeout(timer)
    }
  }, [wishListAdded])

  const handleWishlistClick = async () => {
    const payload = {
      product_id: product?.entity_id,
      qty: 1,
      forceRemoveFromCart: false,
      vendor_id: process?.env?.NEXT_PUBLIC_VENDOR_ID,
      config_option: {},
    }
    console.info('Executing add wishlist item')
    addWishlistItem(payload)
  }

  const handleShareClick = () => {
    setShowShareIcons(!showShareIcons)
  }

  const moveToYoumightAlsolike = () => {
    window.location.href = '#you-might-also-like-section'
  }

  const swiperIcons = [
    {
      icon: '/assets/images/zoom.svg',
      key: 'zoom',
      onClick: () => handleZoomClick(),
    },
    {
      icon: '/assets/images/wishlist.svg',
      key: 'wishlist',
      onClick: () => handleWishlistClick(),
    },
    {
      icon: '/assets/images/share.svg',
      key: 'share',
      onClick: () => handleShareClick(),
    },
  ]

  return (
    <div className="shadow-box  m-1">
      <div className="transition-all duration-300   rounded cursor-pointer shadow-card   w-full xl:flex xl:flex-row-reverse justify-between">
        <div
          className={cn(
            'w-full xl:rtl:mr-5 mb-2.5 md:mb-3 overflow-hidden rounded-md relative',
            thumbnailClassName,
          )}
          style={{ height: 'fit-content' }}
        >
          {!is_in_stock && (
            <div className="absolute origin-center text-white bg-opacity-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 p-3  sm:p-5 bg-brand-dark z-10 w-[180px] sm:w-[240px]   whitespace-nowrap">
              <h1 className="text-center text-md sm:text-lg font-medium">
                Out of Stock
              </h1>
              <p
                onClick={moveToYoumightAlsolike}
                className="hover:underline text-xs sm:text-sm text-center"
              >
                see similar options
              </p>
            </div>
          )}
          <div className="absolute  top-2 right-2 z-10	">
            {swiperIcons.map((icon, index) =>
              icon.key === 'wishlist' && !user ? null : (
                <CarouselActions
                  data={icon}
                  index={index}
                  wishListAdded={wishListAdded}
                  whishListLoading={whishListLoading}
                />
              ),
            )}
            {showShareIcons && <Share image={gallery[0]?.original} />}
          </div>
          <Swiper
            id="productGallery"
            thumbs={{
              swiper:
                thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null,
            }}
            modules={[Navigation, Thumbs]}
            navigation={{
              prevEl: prevRef.current!, // Assert non-null
              nextEl: nextRef.current!, // Assert non-null
            }}
            {...swiperParams}
          >
            {galleryWithFallback?.map((item: any) => (
              <SwiperSlide
                key={`product-gallery-${item.id}`}
                className="flex items-center justify-center"
                style={{ aspectRatio }}
              >
                <Image
                  src={item?.original ?? productGalleryPlaceholder}
                  alt={`Product gallery ${item.id}`}
                  fill={true}
                  priority
                  style={{ objectFit: 'contain' }}
                />

                {!isPlaceholder && (
                  <ThumbnailZoom
                    image={item?.original}
                    aspectRatio={aspectRatio}
                    isPdp={isPdp}
                  />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
          {/* <div className="flex items-center justify-between w-full absolute top-2/4 z-10 px-2.5">
            <div
              ref={prevRef}
              className="flex items-center justify-center text-base transition duration-300 transform -translate-y-1/2 rounded-full cursor-pointer w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 lg:text-lg xl:text-xl bg-brand-light hover:bg-brand hover:text-brand-light focus:outline-none shadow-navigation"
            >
              {dir === 'rtl' ? <IoIosArrowForward /> : <IoIosArrowBack />}
            </div>
            <div
              ref={nextRef}
              className="flex items-center justify-center text-base transition duration-300 transform -translate-y-1/2 rounded-full cursor-pointer w-7 h-7 md:w-8 md:h-8 lg:w-9 lg:h-9 xl:w-10 xl:h-10 lg:text-lg xl:text-xl bg-brand-light hover:bg-brand hover:text-brand-light focus:outline-none shadow-navigation"
            >
              {dir === 'rtl' ? <IoIosArrowBack /> : <IoIosArrowForward />}
            </div>
          </div> */}
        </div>
        {/* End of product main slider */}

        <div
          className={`shrink-0 xl:max-h-[450px] xl:min-h[300px] xl:overflow-y-auto   ${galleryClassName}`}
        >
          <Swiper
            id="productGalleryThumbs"
            onSwiper={setThumbsSwiper}
            spaceBetween={0}
            watchSlidesProgress={true}
            freeMode={true}
            observer={true}
            observeParents={true}
            breakpoints={galleryCarouselBreakpoints}
          >
            {galleryWithFallback?.map((item: any, index) => (
              <SwiperSlide
                key={`product-thumb-gallery-${item.id}`}
                id={`${index}`}
                // onClick={(e:MouseEvent<HTMLImageElement>) => {

                // }}
                onClick={(e: React.MouseEvent<HTMLImageElement>) => {
                  setSelectedImg(e.currentTarget.id)
                }}
                test-id={selectedImg + ' ---- ' + index}
                className={` ${
                  selectedImg === `${index}` ? 'border-brand border' : ''
                }  flex w-[100px]  items-center  justify-center overflow-hidden transition shadow-card   cursor-pointer border-border-base hover:opacity-75`}
                style={{ aspectRatio: '23 / 20' }}
              >
                <Image
                  src={item?.thumbnail ?? productGalleryPlaceholder}
                  alt={`Product thumb gallery ${item.id}`}
                  width={100}
                  height={40}
                  className="p-3.5 py-2"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </div>
  )
}

export default ThumbnailCarousel
